import { Field, ID, InputType } from 'type-graphql'
import GraphQLJSON from 'graphql-type-json'
import { ActionArgs } from '../../action/ActionArgs'
import type { AiUsage } from '../args/AiUsage'

@InputType()
export class RecordAiUsageArgs extends ActionArgs {
  /** Generic owner of this usage (a `User._id` or `Tenant._id`, per `resolveBillingOwnerId`). */
  @Field(() => ID)
  ownerId: string

  /** Caller-supplied feature tag (e.g. `quickscore.processScan`). */
  @Field(() => String)
  feature: string

  /** Token usage + resolved model from the underlying AI call (see `AnalyzeImage`/`AnalyzeImages`). */
  @Field(() => GraphQLJSON)
  usage: AiUsage
}

/**
 * The full charged snapshot for one metered AI call — everything `DebitCredits` persists onto
 * the single `AiCreditTransaction` row `RecordAndDebitAiUsage` writes. No document is created by
 * this action itself; `credits`/`billedCcy`/etc. are a point-in-time snapshot so a later
 * `ModelRate`/`CurrencyRate` change never rewrites the price of a call already charged
 * (`backend-entity-style-guide.md` §5.1).
 */
export type RecordAiUsageResult = {
  /** Credits to debit for this call, in the owner's account currency. */
  credits: number
  currency: string
  model: string
  inputTokens: number
  outputTokens: number
  /** Raw USD cost from the model's canonical `ModelRate`, before FX/markup. */
  rawUsd: number
  unitsPerUsd: number
  markupPct: number
  /** `rawUsd` converted to the account currency (before markup). */
  rawCcy: number
  /** `rawCcy` after markup — the amount actually charged. */
  billedCcy: number
}

/**
 * Prices a single metered AI call — a pure pricing computation, no persistence. Module-internal
 * to `ai-node` — injected by class (composed by `RecordAndDebitAiUsage`), never barrel-exported
 * and never bound to an `IocKey` symbol (`backend-style-guide.md` — "Never export actions/validators").
 */
export interface IRecordAiUsage {
  execute(args: RecordAiUsageArgs): Promise<RecordAiUsageResult>
}
